import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { GlobalBannerService } from '../shared/components/global-banner/global-banner.service';
import { IDocumentState } from './document-data.reducer';
import { IDocumentDataDispatcher } from './document-data-dispatcher.interface';
import {
  DocumentTypesEnum,
  ICopyDocumentOptions,
  IEditTaxRateProperties,
} from './document-data.data';
import { DocumentDataStore } from './document-data.store';
import { DocumentDataBuilder } from './document-data.builder';
import { Document } from './document.model';
import { EntityDataDispatcher } from '../entity-data/entity-data.dispatcher';
import { combineLatest, Observable } from 'rxjs';
import { List } from 'immutable';
import { v4 } from 'uuid';
import {
  addDocumentItemToEditing,
  batchAddDocumentItemsToEditing,
  copyDocument,
  deleteDocumentItemFromEditing,
  deleteFromTmpCache,
  enableCardPayments,
  goToDocumentAddClientScreen,
  ICopyDocument,
  moveDocumentItemInEditing,
  printDocument,
  sendDocument,
  setInTmpCache,
  updateDocumentItemInEditing,
  updateTaxInEditing,
} from './document-data.actions';
import { EntityTypesEnum } from '../../app.enum';
import { omit } from 'lodash';
import { NavigationExtras } from '@angular/router';
import { DocumentItem } from './sub-models/document-item.model';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { delay, map } from 'rxjs/operators';
import {
  fetchList,
  setEditingEntityContent,
} from '../entity-data/entity-data.actions';
import { IMessageDeliveryData } from '../message-delivery/message-delivery.interface';
import { ISearchParams } from '../entity-data/entity-data.data';

// should only be used as a superclass; this class is not injectable
export class DocumentDataDispatcher<
    T extends IDocumentState,
    U extends Document
  >
  extends EntityDataDispatcher<T, U>
  implements IDocumentDataDispatcher {
  protected documentType: DocumentTypesEnum;

  constructor(
    protected store: Store<any>,
    protected documentDataStore: DocumentDataStore<T, U>,
    protected settingsDataStore: SettingsDataStore,
    protected documentDataBuilder: DocumentDataBuilder<U>,
    protected globalBannerService: GlobalBannerService,
    protected translateService: TranslateService
  ) {
    super(
      store,
      documentDataStore,
      documentDataBuilder,
      globalBannerService,
      translateService
    );
  }

  fetchList(
    listFilter: string,
    mergeEntities?: boolean,
    searchParams?: ISearchParams
  ) {
    this.store.dispatch(
      fetchList(this.entityType, searchParams, listFilter, mergeEntities)
    );
  }

  printDocument(document: U, setEditingDocument: boolean): Observable<U> {
    const dispatchId = v4();

    this.store.dispatch(
      printDocument(dispatchId, this.entityType, document, setEditingDocument)
    );

    return this.documentDataStore.getDispatchedResponse<U>(dispatchId);
  }

  copyDocument(copyDocumentOptions: ICopyDocumentOptions): Observable<U> {
    const dispatchId = v4();
    const validOptions: any = omit(copyDocumentOptions, 'copyAs');
    const copyDocumentPayload: ICopyDocument = {
      ...validOptions,
      dispatchId,
      entityType: EntityTypesEnum[copyDocumentOptions.copyAs], // entityType set as copyAs so it targets the
      // destination document store
    };

    this.store.dispatch(copyDocument(copyDocumentPayload));

    return this.documentDataStore.getDispatchedResponse<U>(dispatchId);
  }

  goToDocumentAddClientScreen(
    documentId: string,
    navigationExtras: NavigationExtras = {}
  ) {
    this.store.dispatch(
      goToDocumentAddClientScreen(this.entityType, documentId, navigationExtras)
    );
  }

  addDocumentItemToEditing(documentItem: DocumentItem) {
    this.store.dispatch(
      addDocumentItemToEditing(this.entityType, documentItem)
    );
  }

  updateDocumentItemInEditing(documentItem: DocumentItem) {
    this.store.dispatch(
      updateDocumentItemInEditing(this.entityType, documentItem)
    );
  }

  deleteDocumentItemFromEditing(documentItem: DocumentItem) {
    this.store.dispatch(
      deleteDocumentItemFromEditing(this.entityType, documentItem)
    );
  }

  moveDocumentItemInEditing(
    oldIndex: number,
    newIndex: number,
    isPart: boolean
  ) {
    this.store.dispatch(
      moveDocumentItemInEditing(this.entityType, oldIndex, newIndex, isPart)
    );
  }

  batchAddDocumentItemsToEditing(documentItems: List<DocumentItem>) {
    this.store.dispatch(
      batchAddDocumentItemsToEditing(this.entityType, documentItems)
    );
  }

  updateTaxInEditing(editTaxRateProperties: IEditTaxRateProperties) {
    this.store.dispatch(
      updateTaxInEditing(this.entityType, editTaxRateProperties)
    );
  }

  removeDepositInEditing(saveEditing: boolean): Observable<U> {
    const dispatchId = v4();
    const props = this.documentDataBuilder.buildDocumentContentPropsWithNoDeposit();

    this.store.dispatch(
      setEditingEntityContent(dispatchId, this.entityType, props, saveEditing)
    );

    return this.documentDataStore.getDispatchedResponse<U>(dispatchId);
  }

  setInTmpCache(key: string, value: any) {
    this.store.dispatch(setInTmpCache(this.entityType, key, value));
  }

  deleteFromTmpCache(key: string) {
    this.store.dispatch(deleteFromTmpCache(this.entityType, key));
  }

  enableCardPayments(
    documentId: string,
    href: string,
    setEditingDocument: boolean
  ): Observable<Document> {
    const dispatchId = v4();

    this.store.dispatch(
      enableCardPayments(
        dispatchId,
        this.entityType,
        documentId,
        href,
        setEditingDocument
      )
    );

    return combineLatest([
      this.documentDataStore.getDispatchedResponse<Document>(dispatchId),
      this.settingsDataStore.getSettingsDispatchedResponse(dispatchId),
    ]).pipe(
      map((dispatchedResponses: any[]) => dispatchedResponses[0]),
      delay(200)
    );
  }

  sendDocument(
    document: Document,
    bankTransferFeatureEnabled: boolean,
    cardPaymentsFeatureEnabled: boolean,
    paypalFeatureEnabled: boolean,
    messageDeliveryData: IMessageDeliveryData
  ): Observable<U> {
    const dispatchId = v4();

    this.store.dispatch(
      sendDocument(
        dispatchId,
        this.entityType,
        document,
        bankTransferFeatureEnabled,
        cardPaymentsFeatureEnabled,
        paypalFeatureEnabled,
        messageDeliveryData
      )
    );

    return this.documentDataStore.getDispatchedResponse<U>(dispatchId);
  }
}
